import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Container,
  InputLabel,
  Paper,
  CircularProgress,
  Fade,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  useMediaQuery,
  IconButton,
  InputAdornment,
  StepConnector,
  stepConnectorClasses,
  styled,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon,
  Visibility,
  VisibilityOff,
  AssignmentInd as AssignmentIndIcon,
  ContactPhone as ContactPhoneIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { register, addBeneficiary } from '../services/api_service';
import { useSearchParams } from 'react-router-dom';
import { useAlert } from '../AlertContext';

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
    [theme.breakpoints.down('sm')]: {
      top: 17,
    },
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        'linear-gradient( 95deg,rgb(148,183,84) 0%, rgb(125,166,71) 50%, rgb(100,130,50) 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        'linear-gradient( 95deg,rgb(148,183,84) 0%, rgb(125,166,71) 50%, rgb(100,130,50) 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
  },
}));

const ColorlibStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  transition: 'all 0.3s ease',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient( 135deg, #94B754 0%, #7DA647 100%)',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
    transform: 'scale(1.1)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient( 135deg, #94B754 0%, #7DA647 100%)',
  }),
  [theme.breakpoints.down('sm')]: {
    width: 35,
    height: 35,
    '& .MuiSvgIcon-root': {
      fontSize: '1.1rem',
    },
  },
}));

function ColorlibStepIcon(props) {
  // eslint-disable-next-line react/prop-types
  const { active, completed, className, icon } = props;

  const icons = {
    1: <AssignmentIndIcon />,
    2: <ContactPhoneIcon />,
    3: <SettingsIcon />,
    4: <CheckCircleIcon />,
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {icons[String(icon)]}
    </ColorlibStepIconRoot>
  );
}

const ModernRegistration = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // Added
  const [activeStep, setActiveStep] = useState(0);
  const [state, setState] = useState({
    identification_type: 'CC',
    privacy_policy: false,
    send_notifications_email: true,
    send_notifications_whatsapp: true,
    membership: 'NO AFILIADO',
    identification_number_titular: searchParams.get('idx'),
  });

  const session = JSON.parse(localStorage.getItem('session') || '{}');
  const [userType, setUserType] = useState(1); // 1.TITULAR; 2.BENEFICIARIO
  const { showAlert } = useAlert();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isBeneficiary = !!searchParams.get('idx');
  const isAdmin = session.is_admin;

  const steps = [
    'Información Personal',
    'Contacto',
    'Preferencias',
    'Confirmación',
  ];

  const onChange = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const getStepError = (step) => {
    switch (step) {
      case 0:
        if (!state.name) {
          return 'Por favor completa todos los campos requeridos';
        }
        return null;
      case 1:
        if (!state.password) {
          return 'Por favor completa todos los campos requeridos';
        }
        // eslint-disable-next-line no-case-declarations
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (state.email && !emailRegex.test(state.email)) {
          return 'Por favor ingresa un correo electrónico válido';
        }
        // eslint-disable-next-line no-case-declarations
        const phoneRegex = /^\d+$/;
        if (state.phone && !phoneRegex.test(state.phone)) {
          return 'El número de celular debe contener solo números';
        }
        if (state.password.length < 6) {
          return 'La contraseña debe tener al menos 6 caracteres';
        }
        return null;
      case 2:
        if (!state.privacy_policy) {
          return 'Debes aceptar las políticas de privacidad y términos de uso';
        }
        return null;
      default:
        return null;
    }
  };

  const handleNext = () => {
    const error = getStepError(activeStep);
    if (!error) {
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      showAlert(error, 'warning');
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleRegistration = async () => {
    // Final validation of all previous steps
    for (let i = 0; i < steps.length - 1; i++) {
      const error = getStepError(i);
      if (error) {
        showAlert(error, 'warning');
        setActiveStep(i);
        return;
      }
    }

    if (!state.privacy_policy) {
      showAlert(
        'Debes aceptar las políticas de privacidad y términos de uso',
        'warning'
      );
      return;
    }

    setLoading(true);
    try {
      if (!state.identification_number_titular) {
        await register(state);
        showAlert('Registro exitoso', 'success');
        window.location.pathname = isAdmin ? '/admin-users' : '/login';
      } else {
        const response = await register({ ...state, beneficiary_user: true });
        if (response) {
          await addBeneficiary({
            user_id: response.id,
            identification_number_titular: state.identification_number_titular,
            phone: state.phone,
            email: state.email,
          });
          showAlert('Beneficiario añadido correctamente', 'success');
          const redirectTo = searchParams.get('redirect_to');
          window.location.pathname = isAdmin
            ? '/admin-users'
            : redirectTo
              ? `/${redirectTo}`
              : '/';
        }
      }
    } catch (error) {
      if (error?.includes('violates unique constraint')) {
        showAlert('El usuario ya está inscrito', 'error');
      } else {
        showAlert('Error durante el registro', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {isAdmin && (
              <FormControl fullWidth>
                <InputLabel>Tipo de usuario</InputLabel>
                <Select
                  value={userType}
                  label="Tipo de usuario"
                  onChange={(e) => setUserType(e.target.value)}
                >
                  <MenuItem value={1}>TITULAR</MenuItem>
                  <MenuItem value={2}>BENEFICIARIO</MenuItem>
                </Select>
              </FormControl>
            )}

            {isAdmin && userType === 2 && (
              <TextField
                label="Número de Identificación del Titular"
                fullWidth
                value={state.identification_number_titular || ''}
                onChange={(e) =>
                  onChange('identification_number_titular', e.target.value)
                }
              />
            )}

            <TextField
              label="Nombre Completo"
              fullWidth
              required
              value={state.name || ''}
              onChange={(e) => onChange('name', e.target.value)}
              helperText="Ingresa tu nombre completo tal como aparece en tu documento"
            />

            <FormControl fullWidth>
              <InputLabel>Tipo de Identificación</InputLabel>
              <Select
                value={state.identification_type}
                label="Tipo de Identificación"
                onChange={(e) =>
                  onChange('identification_type', e.target.value)
                }
              >
                <MenuItem value="CC">Cédula de ciudadanía</MenuItem>
                <MenuItem value="CE">Cédula de extranjería</MenuItem>
                <MenuItem value="TI">Tarjeta de identidad</MenuItem>
                {/*<MenuItem value="NIT">NIT</MenuItem>
                <MenuItem value="PB">Pasaporte</MenuItem>*/}
              </Select>
            </FormControl>

            <TextField
              label="Número de Identificación"
              fullWidth
              value={state.identification_number || ''}
              onChange={(e) =>
                onChange('identification_number', e.target.value)
              }
            />

            {isAdmin && userType === 1 && (
              <TextField
                label="Serial"
                fullWidth
                value={state.serial || ''}
                onChange={(e) => onChange('serial', e.target.value)}
              />
            )}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Correo Electrónico"
              fullWidth
              required
              type="email"
              value={state.email || ''}
              onChange={(e) => onChange('email', e.target.value)}
              error={
                state.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)
              }
              helperText={
                state.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)
                  ? 'Correo electrónico inválido'
                  : ''
              }
            />

            <TextField
              label="Número de Celular"
              fullWidth
              value={state.phone || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  onChange('phone', value);
                }
              }}
              placeholder="3001234567"
            />

            <TextField
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              required
              value={state.password || ''}
              onChange={(e) => onChange('password', e.target.value)}
              helperText="Mínimo 6 caracteres"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preferencias de notificaciones
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={state.privacy_policy}
                  onChange={(e) => onChange('privacy_policy', e.target.checked)}
                  required
                />
              }
              label="Acepto las políticas de privacidad y términos de uso"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={state.send_notifications_email}
                  onChange={(e) =>
                    onChange('send_notifications_email', e.target.checked)
                  }
                />
              }
              label="Recibir notificaciones por correo electrónico"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={state.send_notifications_whatsapp}
                  onChange={(e) =>
                    onChange('send_notifications_whatsapp', e.target.checked)
                  }
                />
              }
              label="Recibir notificaciones por WhatsApp"
            />
          </Box>
        );

      default:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: '#94B754', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
              ¡Todo listo!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {isBeneficiary
                ? 'Serás añadido como beneficiario del plan.'
                : 'Tu cuenta será creada y podrás acceder al sistema.'}
            </Typography>
            <Box
              sx={{
                textAlign: 'left',
                maxWidth: 400,
                mx: 'auto',
                p: 3,
                borderRadius: 3,
                bgcolor: 'rgba(148, 183, 84, 0.05)',
                border: '1px solid rgba(148, 183, 84, 0.1)',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#7DA647',
                  borderBottom: '2px solid',
                  borderColor: 'rgba(125, 166, 71, 0.2)',
                  pb: 0.5,
                  display: 'inline-block',
                }}
              >
                Resumen de registro
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ color: '#666' }}>Nombre:</span>
                  <strong style={{ color: '#333', textAlign: 'right' }}>
                    {state.name}
                  </strong>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ color: '#666' }}>Documento:</span>
                  <strong style={{ color: '#333', textAlign: 'right' }}>
                    {state.identification_type} {state.identification_number}
                  </strong>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ color: '#666' }}>Email:</span>
                  <strong style={{ color: '#333', textAlign: 'right' }}>
                    {state.email}
                  </strong>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ color: '#666' }}>Teléfono:</span>
                  <strong style={{ color: '#333', textAlign: 'right' }}>
                    {state.phone}
                  </strong>
                </Typography>
              </Box>
            </Box>
          </Box>
        );
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: isMobile ? 1 : 2,
      }}
    >
      <Container maxWidth="md" sx={{ px: isMobile ? 1 : 2 }}>
        <Fade in={true} timeout={800}>
          <Paper
            elevation={24}
            sx={{
              borderRadius: isMobile ? 3 : 4,
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                color: 'white',
                p: isMobile ? 3 : 4,
                textAlign: 'center',
              }}
            >
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                sx={{ fontWeight: 'bold', mb: 1 }}
              >
                {isBeneficiary ? 'Añadir Beneficiario' : 'Crear Cuenta'}
              </Typography>
            </Box>

            {/* Stepper */}
            <Box sx={{ p: isMobile ? 2 : 3, pb: 0 }}>
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                connector={<ColorlibConnector />}
              >
                {steps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel
                      StepIconComponent={ColorlibStepIcon}
                      sx={{
                        '& .MuiStepLabel-label': {
                          display: isMobile ? 'none' : 'block',
                          fontSize: '0.875rem',
                          mt: 1,
                          color:
                            activeStep === index ? '#7DA647' : 'text.secondary',
                          fontWeight: activeStep === index ? 'bold' : 'normal',
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
              {isMobile && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                    }}
                  >
                    Paso {activeStep + 1} de {steps.length}
                  </Typography>
                  <Fade in={true} key={activeStep}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#7DA647',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        lineHeight: 1.2,
                      }}
                    >
                      {steps[activeStep]}
                    </Typography>
                  </Fade>
                </Box>
              )}
            </Box>

            {/* Content */}
            <Box sx={{ p: isMobile ? 3 : 4 }}>
              {renderStepContent(activeStep)}
            </Box>

            {/* Actions */}
            <Box
              sx={{
                p: isMobile ? 3 : 4,
                pt: 0,
                display: 'flex',
                flexDirection: isMobile ? 'column-reverse' : 'row',
                gap: 2,
                justifyContent: 'space-between',
              }}
            >
              <Button
                onClick={handleBack}
                disabled={activeStep === 0}
                startIcon={<ArrowBackIcon />}
                fullWidth={isMobile}
                sx={{
                  visibility: activeStep === 0 ? 'hidden' : 'visible',
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'bold',
                }}
              >
                Atrás
              </Button>

              {!isMobile && <Box sx={{ flex: '1 1 auto' }} />}

              <Button
                variant="contained"
                onClick={
                  activeStep === steps.length - 1
                    ? handleRegistration
                    : handleNext
                }
                disabled={loading}
                fullWidth={isMobile}
                startIcon={
                  activeStep === steps.length - 1 ? (
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <PersonAddIcon />
                    )
                  ) : null
                }
                sx={{
                  background:
                    'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                  px: isMobile ? 2 : 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(148, 183, 84, 0.3)',
                  '&:hover': {
                    background:
                      'linear-gradient(135deg, #7DA647 0%, #6A8E3D 100%)',
                  },
                }}
              >
                {activeStep === steps.length - 1
                  ? loading
                    ? 'Registrando...'
                    : isBeneficiary
                      ? 'Añadir Beneficiario'
                      : 'Crear Cuenta'
                  : 'Siguiente'}
              </Button>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default ModernRegistration;
